import { useState, useCallback, useEffect, useRef, useMemo, use } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { API, ShareAPI } from '../../API';
import { Paginator } from '../Paginator/Paginator';
import { Tabs } from '../Tabs/Tabs';
import { NarrativeSourcesTable } from './NarrativeSourcesTable';
import { ModalMessagesTable } from './ModalMessagesTable';
import { ampli } from '../../ampli';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { platformNamesMapping } from '../../utils/platforms';
import { ReactComponent as AddToIcon } from '../../assets/addTo.svg';
import { ReactComponent as SourceGroupIcon} from '../../pages/Layout/assets/database.svg';
import { ReactComponent as ChevronIcon} from '../../components/Filters/assets/chevron.svg';
import { replaceNewlinesWithBreaks } from '../../utils/replaceNewlinesWithBreaks';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu';
import { Radiobutton } from '../DropdownMenu/Radiobutton';
import { ReactComponent as SortIcon } from '../../assets/sort.svg';
import { ModalOpinionsTable } from './ModalOpinionsTable';
import { ActorDetails } from './ActorDetails';
import { showRequestError } from '../../utils/requestError';
import {
  narrativeActorsUrl,
  narrativeMessagesSearchUrl,
  rollupServesModal,
  toMessagesSearchBody,
  toRollupActorsParams,
  useNarrativeContentModalsEs,
} from '../../utils/narrativeActors';
import {
  buildCommonParams,
  appendExcludeFilters,
  buildFetchConfig,
  buildExcludeFlags,
} from './widgetContentModalUtils';

import styles from './WidgetContentModal.module.scss';

// `messages/search` pages by cursor (and echoes `page: null`); v1 lists page by number.
const isCursorPaged = (data) => Boolean(data) && data.page == null && 'cursor' in data;

const hasMoreCursorPages = (data) =>
  isCursorPaged(data) && Boolean(data.cursor) && (data.objects?.length ?? 0) < data.total;

const appendCursorPage = (previous, data, cursor) =>
  cursor && previous?.objects ? { ...data, objects: [...previous.objects, ...data.objects] } : data;

export const WidgetContentModal = ({
  title='',
  tabIndex,
  infoModal,
  isChart=false,
  type=null,
  subType=null,
  platformOption,
  platformsOptions,
  stateAffiliatedId,
  stateAffiliatedExclude,
  affiliationTypes,
  affiliationTypesExclude,
  countryId=null,
  audienceRange,
  sourceGroup,
  tags=null,
  source,
  language,
  narrative,
  sentimentOptions,
  eventIdsOptions,
  opinionIdsOptions,
  startDate,
  endDate,
  contentTypeOptions,
  onChange,
  isShare = false,
  opinion,
  event,
  onAddToCase,
  onAddToGroup,
  showAddToActions=false,
  countryIdExclude,
  languageCodeExclude,
  highRiskExclude,
  botsExclude,
  sourceGroupExclude,
  widgetName,
}) => {
  const { t } = useTranslation();
  const [tablistIndex, setTablistIndex] = useState(tabIndex ? tabIndex : (source || sourceGroup) ? 1  : 0); 
  const [currentUser] = useCurrentUser();
  const nativeLang = currentUser?.translation_language ? currentUser?.translation_language : window.clientInformation.language.split('-', 1)[0].toUpperCase();

  const narrativeWidgetConfigOpinions = narrative?.widget_config?.opinion_card || null;
  
  const sourcesTypes = [
    { value: null, label: t('All') },
    { value: 'is_verified', label: t('Verified') },
    { value: 'influential', label: t('Influential') },
    { value: 'state_affiliated', label: t('State-affiliated') },
    { value: 'discreditated', label: t('Compromised accounts') },
    { value: 'is_bot', label: t('Bots') },
    { value: 'audience', label: t('Audience')}
  ];

  const [platformOptions, setPlatformOptions] = useState(() => [
    { value: null, label: t('All') },
  ]);
  // useEffect(() => setPlatformOptions(platformsOptions),[])
  const [platform, setPlatform] = useState(platformOption || null);

  const [sourceType, setSourceType] = useState(type || sourcesTypes[0]);
  const [stateAffiliatedID, setStateAffiliatedID] = useState(stateAffiliatedId || null)
  const [stateAffiliatedExcludeFilter, setStateAffiliatedExcludeFilter] = useState(stateAffiliatedExclude);
  const [stateAffiliatedTypes, setStateAffiliatedTypes] = useState(affiliationTypes || null);
  const [stateAffiliatedTypesExclude, setStateAffiliatedTypesExclude] = useState(affiliationTypesExclude);
  const [highRiskExcludeFilter, setHighRiskExcludeFilter] = useState(highRiskExclude || false);
  const [botsExcludeFilter, setBotsExcludeFilter] = useState(botsExclude || false);
  const [countryID, setCountryID] = useState(countryId || null);
  const [sourceSubType, setSourceSubType] = useState(subType || null);
  const [narrativeSources, setNarrativeSources] = useState(null);
  const [narrativeMessages, setNarrativeMessages] = useState(null);
  const [sourcesSorting, setSourcesSorting] = useState('-audience');
  const [messagesSorting, setMessagesSorting] = useState('-date_publicated');
  const [isSourcesLoading, setIsSourcesLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Bumped on every fresh (non-cursor) request so a slow Load more response of an older query is dropped.
  const messagesRequestRef = useRef(0);
  const actorMessagesRequestRef = useRef(0);
  const [audience, setAudience] = useState(audienceRange || null);
  const [sourceGroupIds, setSourceGroupIds] = useState(sourceGroup || null);
  const [sourceGroupIdsExclude, setSourceGroupIdsExclude] = useState(sourceGroupExclude);
  const [tagPaths, setTagPaths] = useState(tags || null);
  const [sourceId, setSourceId] = useState(source || null);
  const [eventIds, setEventIds] = useState(eventIdsOptions || null);
  const [sentiment, setSentiment] = useState(sentimentOptions || null);
  const [start, setStart] = useState(startDate || null);
  const [end, setEnd] = useState(endDate || null);
  const [languages, setLanguages] = useState(language || null);
  const [opinionIds, setOpinionIds] = useState(opinionIdsOptions || null);
  const [contentTypes, setContentTypes] = useState(contentTypeOptions || null);
  const [countriesExclude, setCountriesExclude] = useState(countryIdExclude);
  const [languagesExclude, setLanguagesExclude] = useState(languageCodeExclude);
  const [opinions, setOpinions] = useState(null);
  const [isOpinionsLoading, setIsOpinionsLoading] = useState(false);
  const [isBulkTranslationLoading, setIsBulkTranslationLoading] = useState(false);
  const [isBulkTranslation, setIsBulkTranslation] = useState(false);
  const [sourceData, setSourceData] = useState(null);
  const [showSourceDetails, setShowSourceDetails] = useState(false);
  const [sourceDetailsId, setSourceDetailsId] = useState(null);
  const [actorMessages, setActorMessages] = useState(null);
  const [actorMessagesSorting, setActorMessagesSorting] = useState('-date_publicated');
  const [isActorMessagesLoading, setIsActorMessagesLoading] = useState(false);
  const [isActorBulkTranslationLoading, setIsActorBulkTranslationLoading] = useState(false);
  const [isActorBulkTranslation, setIsActorBulkTranslation] = useState(false);


  const [dropdownMenu, setDropdownMenu] = useState(false);
  let translationControllerRef = useRef();
  let actorTranslationControllerRef = useRef();

  useEffect(() => {
    if (sourceId) {
      setSourceDetailsId(sourceId[0]);
    }
  }, [sourceId]);

  const api = isShare ? ShareAPI : API;
  const contentModalsEs = useNarrativeContentModalsEs(isShare);

  const fetchSource = useCallback(() => {
    api.fetch('GET', `/API/v1/sources/${sourceDetailsId}`).then(setSourceData).catch(error => {
      setSourceData(null);
      showRequestError(error, t('Actor details'));
    });
  }, [sourceDetailsId]);

  useEffect(() => {
    if (!sourceDetailsId || isShare) return;
    setShowSourceDetails(true);
    fetchSource()
  }, [sourceDetailsId, isShare]);

  const handleCloseSourceDetails = () => {
    // Cancel any ongoing actor translations
    if (actorTranslationControllerRef.current) {
      actorTranslationControllerRef.current.abort();
    }
    // Drop the response of a pending actor request so it cannot repopulate the closed view.
    actorMessagesRequestRef.current += 1;
    setIsActorMessagesLoading(false);
    setIsLoadingMore(false);
    setShowSourceDetails(false);
    setSourceData(null);
    setSourceDetailsId(null);
    setActorMessages(null);
    setActorMessagesSorting('-date_publicated');
    setIsActorBulkTranslation(false);
    setIsActorBulkTranslationLoading(false);
  };

  const fetchTopicSourcesStats = useCallback(() => {
    if (!narrative) return;

    const urlParams = new URLSearchParams();

    urlParams.append('ids', narrative.id);

    api.fetch(
      'GET',
      `/API/v1/sources/stats/NARRATIVE?${urlParams.toString()}`,
      null,
      null,
    ).then((data) => {
      // setNarrativeSourcesStats(data);

      const platforms = Object.fromEntries(
        data?.objects?.map((val) => [
          val.stats.source_type,
          val.stats.source_type,
        ]),
      );

      setPlatformOptions([
        { value: null, label: 'All' },
        ...Object.keys(platforms).map((k) => ({
          value: k,
          label: platformNamesMapping[k],
        })),
      ]);
    }).catch((e) => showRequestError(e, t('Platform filter')));
  }, []);

  useEffect(() => {
    return () => {
      if (translationControllerRef.current) {
        translationControllerRef.current.abort();
      }
    };
  }, []);

  // useEffect(fetchTopicSourcesStats, [fetchTopicSourcesStats]);

  // useEffect(() => {
  //   if(!platformsOptions) {
  //     fetchTopicSourcesStats()
  //   }
  //   ampli.track({
  //     event_type: 'Open actors popup',
  //     event_properties: {
  //       user_id: currentUser?.id,
  //       workspace_id: currentUser?.workspace_id,
  //       narrative_id: narrative?.id,
  //       actor_type: type,
  //     }
  //   });
  // }, [])

  const fetchOpinions = (countryId, page=1) => {
    setIsOpinionsLoading(true);
    let size = 10;
    if (opinions !== null) {
      setOpinions(null);
    }
    const urlParams = new URLSearchParams();

    urlParams.set('size', size);
    urlParams.set('page', page);
    
    
    urlParams.set('sort', '-messages_count');
    
    
    if (countryId.length > 0) {
      countryId.forEach((country) => {
        urlParams.append('source_country_ids', country.value);
      });
    }
    api
      .fetch(
        'GET',
        `/API/v1/topic-modeling/${narrative?.id}/events/${
          eventIdsOptions[0]
        }/opinions?${urlParams.toString()}`,
      )
      .then((data) => {
        setOpinions(data);
        setIsOpinionsLoading(false);
      }).catch((e) => {
        setIsOpinionsLoading(false);
        showRequestError(e, t('Opinions'));
      });
  };

  useEffect(() => {
    if(eventIdsOptions && countryID) {
      fetchOpinions(countryID);
    }
    
  }, [eventIdsOptions]);


  const fetchSources = useCallback(
    (config, excludeFlags, page = 1) => {
      if (!narrative?.id) return;

      const urlParams = buildCommonParams(
        {
          ...config,
          narrativeId: narrative.id,
          sorting: config.sorting,
          stateAffiliatedID,
          page,
        },
        { audienceParamPrefix: '', countryIdParam: 'country_ids' }
      );

      // Source-specific: ids parameter
      if (config.sourceId) {
        urlParams.set('ids', config.sourceId);
      }

      // Append exclude filters
      appendExcludeFilters(urlParams, excludeFlags, 'sources');

      setIsSourcesLoading(true);

      const url = rollupServesModal(urlParams, contentModalsEs)
        ? narrativeActorsUrl(narrative.id, 'actors', toRollupActorsParams(urlParams))
        : `/API/v1/sources?${urlParams.toString()}`;

      api
        .fetch('GET', url)
        .then((data) => {
          setNarrativeSources(data);
        })
        .catch((error) => {
          showRequestError(error, t('Actors'));
        })
        .finally(() => {
          setIsSourcesLoading(false);
        });
    },
    [narrative?.id, stateAffiliatedID, api, t, contentModalsEs]
  );

  // Memoized config objects to prevent unnecessary re-renders
  const sourcesConfig = useMemo(() => buildFetchConfig({
    narrativeId: narrative?.id,
    sourceType,
    sourceSubType,
    platform,
    countryID,
    sorting: sourcesSorting,
    audience,
    sourceGroup: sourceGroupIds,
    tags: tagPaths,
    sourceId,
    eventIds,
    opinionIds,
    sentiment,
    start,
    end,
    contentTypes,
    languages,
    stateAffiliatedID,
    affiliatedTypes: stateAffiliatedTypes,
    widgetName,
  }), [
    narrative?.id, sourceType, sourceSubType, platform, countryID,
    sourcesSorting, audience, sourceGroupIds, tagPaths, sourceId, eventIds,
    opinionIds, sentiment, start, end, contentTypes, languages,
    stateAffiliatedID, stateAffiliatedTypes, widgetName,
  ]);

  const messagesConfig = useMemo(() => buildFetchConfig({
    narrativeId: narrative?.id,
    sourceType,
    sourceSubType,
    platform,
    countryID,
    sorting: messagesSorting,
    audience,
    sourceGroup: sourceGroupIds,
    tags: tagPaths,
    sourceId,
    eventIds,
    opinionIds,
    sentiment,
    start,
    end,
    contentTypes,
    languages,
    stateAffiliatedID,
    affiliatedTypes: stateAffiliatedTypes,
    widgetName,
  }), [
    narrative?.id, sourceType, sourceSubType, platform, countryID,
    messagesSorting, audience, sourceGroupIds, tagPaths, sourceId, eventIds,
    opinionIds, sentiment, start, end, contentTypes, languages,
    stateAffiliatedID, stateAffiliatedTypes, widgetName,
  ]);

  const excludeFlags = useMemo(() => buildExcludeFlags({
    countriesExclude,
    languagesExclude,
    stateAffiliatedExclude: stateAffiliatedExcludeFilter,
    stateAffiliationTypesExclude: stateAffiliatedTypesExclude,
    highRiskExclude: highRiskExcludeFilter,
    botsExclude: botsExcludeFilter,
    sourceGroupExclude: sourceGroupIdsExclude,
  }), [
    countriesExclude, languagesExclude, stateAffiliatedExcludeFilter,
    stateAffiliatedTypesExclude, highRiskExcludeFilter, botsExcludeFilter,
    sourceGroupIdsExclude,
  ]);

  useEffect(() => {
    fetchSources(sourcesConfig, excludeFlags);
  }, [fetchSources, sourcesConfig, excludeFlags]);

  const fetchNarrativeMessages = useCallback(
    (config, excludeFlags, page = 1, options = {}) => {
      if (!narrative?.id) return;

      const { shouldCancelTranslation = false, cursor = null } = options;

      if (shouldCancelTranslation && translationControllerRef.current) {
        cancelTranslateAll();
        setIsBulkTranslationLoading(false);
        setIsBulkTranslation(false);
      }

      const urlParams = buildCommonParams(
        {
          ...config,
          narrativeId: narrative.id,
          sorting: config.sorting,
          stateAffiliatedID,
          page,
        },
        { audienceParamPrefix: 'source_', countryIdParam: 'country_id' }
      );

      // Messages-specific: source_id parameter
      if (config.sourceId) {
        urlParams.set('source_id', config.sourceId);
      }

      // Append exclude filters
      appendExcludeFilters(urlParams, excludeFlags, 'messages');

      if (cursor) {
        setIsLoadingMore(true);
      } else {
        messagesRequestRef.current += 1;
        setIsMessagesLoading(true);
        setIsLoadingMore(false);
      }
      const generation = messagesRequestRef.current;

      const request = rollupServesModal(urlParams, contentModalsEs)
        ? api.fetch('POST', narrativeMessagesSearchUrl(narrative.id), null, toMessagesSearchBody(urlParams, cursor))
        : api.fetch('GET', `/API/v1/messages?${urlParams.toString()}`);

      request
        .then((data) => {
          if (generation !== messagesRequestRef.current) return;
          setNarrativeMessages((previous) => appendCursorPage(previous, data, cursor));
        })
        .catch((error) => {
          showRequestError(error, t('Publications'));
        })
        .finally(() => {
          if (generation !== messagesRequestRef.current) return;
          setIsMessagesLoading(false);
          setIsLoadingMore(false);
        });
    },
    [narrative?.id, stateAffiliatedID, api, t, contentModalsEs]
  );

  useEffect(() => {
    fetchNarrativeMessages(messagesConfig, excludeFlags, 1, {
      shouldCancelTranslation: true,
    });
  }, [fetchNarrativeMessages, messagesConfig, excludeFlags]);

  // Fetch actor-specific messages
  const fetchActorMessages = useCallback(
    (actorId, sorting, page = 1, cursor = null) => {
      if (!narrative?.id || !actorId) return;

      // Cancel any ongoing actor translations
      if (actorTranslationControllerRef.current) {
        actorTranslationControllerRef.current.abort();
      }
      setIsActorBulkTranslationLoading(false);
      setIsActorBulkTranslation(false);

      const urlParams = buildCommonParams(
        {
          ...messagesConfig,
          sorting,
          page,
        },
        { audienceParamPrefix: 'source_', countryIdParam: 'country_id' }
      );

      // Set the actor's source_id (override any sourceId from messagesConfig)
      urlParams.delete('source_id');
      urlParams.set('source_id', actorId);

      // Append exclude filters
      appendExcludeFilters(urlParams, excludeFlags, 'messages');

      if (cursor) {
        setIsLoadingMore(true);
      } else {
        actorMessagesRequestRef.current += 1;
        setIsActorMessagesLoading(true);
        setIsLoadingMore(false);
      }
      const generation = actorMessagesRequestRef.current;

      const request = rollupServesModal(urlParams, contentModalsEs)
        ? api.fetch('POST', narrativeMessagesSearchUrl(narrative.id), null, toMessagesSearchBody(urlParams, cursor))
        : api.fetch('GET', `/API/v1/messages?${urlParams.toString()}`);

      request
        .then((data) => {
          if (generation !== actorMessagesRequestRef.current) return;
          setActorMessages((previous) => appendCursorPage(previous, data, cursor));
        })
        .catch((error) => {
          showRequestError(error, t('Actor publications'));
        })
        .finally(() => {
          if (generation !== actorMessagesRequestRef.current) return;
          setIsActorMessagesLoading(false);
          setIsLoadingMore(false);
        });
    },
    [narrative?.id, messagesConfig, excludeFlags, api, t, contentModalsEs]
  );

  // Fetch actor messages when actor is selected or sorting changes
  useEffect(() => {
    if (sourceDetailsId) {
      fetchActorMessages(sourceDetailsId, actorMessagesSorting);
    }
  }, [sourceDetailsId, actorMessagesSorting, fetchActorMessages]);

  const handleBulkTranslation = async (messagesToTranslate, abortController) => {
    const BATCH_SIZE = 5;
    const nativeLanguage = currentUser?.translation_language
      ? currentUser?.translation_language
      : window.clientInformation.language.split('-', 1)[0];

    const batches = [];
    for (let i = 0; i < messagesToTranslate.length; i += BATCH_SIZE) {
      batches.push(messagesToTranslate.slice(i, i + BATCH_SIZE));
    }

    for (const batch of batches) {
      if (abortController?.current?.aborted) {
        break;
      }
      try {
        await processBatch(batch, nativeLanguage, abortController);
      } catch (error) {
        if (error.name === 'AbortError') {
          break;
        }
        console.error('Error processing batch:', error);
        // Continue with next batch even if current batch fails
      }
    }
  };

  const processBatch = async (batch, nativeLanguage, abortController) => {
    if (!narrativeMessages || !narrativeMessages.objects) {
      return;
    }

    if (abortController?.current?.aborted) {
      return;
    }
    const translationPromises = batch.map(message => {
      const originalPostText = message.content_type === 'COMMENT' ? replaceNewlinesWithBreaks(message.parent_message?.highlighted_text || message.parent_message?.text) : '';
      const textToTranslate = message.highlighted_text ? replaceNewlinesWithBreaks(message.highlighted_text) : replaceNewlinesWithBreaks(message.text);

      if (originalPostText) {
        return Promise.allSettled([
          translateMessage(message.id, originalPostText, nativeLanguage, null, abortController),
          translateMessage(message.id, textToTranslate, nativeLanguage, null, abortController),
        ]).then(results => ({
          messageId: message.id,
          type: 'comment',
          parentResult: results[0],
          messageResult: results[1]
        }));
      } else {
        return translateMessage(message.id, textToTranslate, nativeLanguage, null, abortController)
          .then(data => ({
            messageId: message.id,
            type: 'message',
            messageResult: { status: 'fulfilled', value: data }
          }))
          .catch(error => ({
            messageId: message.id,
            type: 'message',
            messageResult: { status: 'rejected', reason: error }
          }));
      }
    });

    const batchResults = await Promise.allSettled(translationPromises);

     if (abortController?.current?.aborted) {
      return;
    }

    if (!narrativeMessages || !narrativeMessages.objects) {
      return;
    }
    setNarrativeMessages(prevMessages => {
      let newMessagesList = Array.isArray(prevMessages?.objects) ? [...prevMessages.objects] : [];

      batchResults.forEach(result => {
        if (result.status === 'fulfilled') {
          const { messageId, type, parentResult, messageResult } = result.value;

          newMessagesList = newMessagesList.map(message => {
            if (message?.id === messageId) {
              let updatedMessage = {
                ...message,
                isTranslation: true,
              };

              if (type === 'comment' && parentResult) {
                if (parentResult.status === 'fulfilled') {
                  updatedMessage.parent_message = {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: parentResult.value.destination_text,
                  };
                } else {
                  updatedMessage.parent_message = {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  };
                }
              }

              if (messageResult.status === 'fulfilled') {
                updatedMessage.translated = messageResult.value.destination_text;
              } else {
                updatedMessage.translated = t('We were not able to translate this text.');
                updatedMessage.error = true;
              }

              return updatedMessage;
            }
            return message;
          });
        }
      });

      return { ...prevMessages, objects: newMessagesList };
    });
  };

  const toggleAllToTranslation = (messageIds) => {
    setNarrativeMessages(prevMessages => ({
      ...prevMessages,
      objects: prevMessages.objects.map(message => {
        if (messageIds.includes(message.id)) {
          return {
            ...message,
            isTranslation: true,
            ...(message.content_type === 'COMMENT' && message.parent_message && {
              parent_message: {
                ...message.parent_message,
                isTranslation: true,
              }
            })
          };
        }
        return message;
      })
    }));
  };

  const toggleAllToOriginal = (messageIds) => {
    setNarrativeMessages(prevMessages => ({
      ...prevMessages,
      objects: prevMessages.objects.map(message => {
        if (messageIds.includes(message.id)) {
          return {
            ...message,
            isTranslation: false,
            ...(message.content_type === 'COMMENT' && message.parent_message && {
              parent_message: {
                ...message.parent_message,
                isTranslation: false,
              }
            })
          };
        }
        return message;
      })
    }));
  };

  const handleBulkTranslateClick = async () => {
    setIsBulkTranslationLoading(true);

    const messagesToTranslate = [];
    const alreadyTranslated = [];

    if (translationControllerRef.current) {
      translationControllerRef.current.abort();
    }

    translationControllerRef.current = new AbortController();


    narrativeMessages.objects.forEach(message => {
      if (message.translated) {
        alreadyTranslated.push(message);
      } else {
        messagesToTranslate.push(message);
      }
    });
    
    if (messagesToTranslate.length === 0) {
      console.log('No messages to translate');
      const messageIds = alreadyTranslated.map(msg => msg.id);
      toggleAllToTranslation(messageIds);
      setIsBulkTranslationLoading(false);
      return;
    }

    if ( alreadyTranslated.length > 0) {
      toggleAllToTranslation(alreadyTranslated.map(msg => msg.id));
    }

    const messageIds = messagesToTranslate.map(msg => msg.id);
    toggleAllToTranslation(messageIds);


    try {
      await handleBulkTranslation(messagesToTranslate, translationControllerRef);
      setIsBulkTranslationLoading(false);
    } catch (error) {
      setIsBulkTranslationLoading(false);
      console.error('Error during bulk translation:', error);
    } finally {
      translationControllerRef.current = null;
    }
  };

  const handleShowOriginalClick = () => {
    cancelTranslateAll();
    const translatedMessages = narrativeMessages.objects.filter(message => message.translated);
    const messageIds = translatedMessages.map(msg => msg.id);
    
    toggleAllToOriginal(messageIds);
    setIsBulkTranslationLoading(false);
  };

  const cancelTranslateAll = () => {
    if (translationControllerRef.current) {
      translationControllerRef.current.abort();
    }
  };
  
  
  const translateMessage = (messageId, text, nativeLang, sourceLang='', abortController) => {
    let body;
    if(sourceLang) {
      body = {
        text: text,
        destination_language: nativeLang,
        source_language: sourceLang
      }
    } else {
      body = {
        text: text,
        destination_language: nativeLang,
      }
    }
    return api.fetch('POST', '/API/v1/translations/translate', null, body, abortController?.current?.signal);
  }
  
  const toggleTranslate = (messageId, isTranslation) => {
    setNarrativeMessages({...narrativeMessages, objects: narrativeMessages.objects.map(message => {
      if(message.id === messageId) {
        message.isTranslation = isTranslation;
        return message;
      } else {
        return message;
      }
    })})
  }
  
  const handleTranslation = (messageId, text, isTranslation) => {
    const target = narrativeMessages.objects.find(message => message.id === messageId);
    const originalPostText =
      target?.content_type === 'COMMENT' ? target?.parent_message?.highlighted_text || target?.parent_message?.text : '';
    if (target.translated) {
      toggleTranslate(messageId, isTranslation)
    } else {
      toggleTranslate(messageId, true)
      
      const nativeLanguage = currentUser?.translation_language
        ? currentUser?.translation_language
        : narrative.parameters?.translate_keywords_query
        ? narrative.parameters?.keywords_query_origin_language
        : window.clientInformation.language.split('-', 1)[0];

      let newMessagesList = [...narrativeMessages.objects];

      if (originalPostText) {
        Promise.allSettled([
          translateMessage(messageId, originalPostText, nativeLanguage),
          translateMessage(messageId, text, nativeLanguage),
        ]).then((results) => {
          if (results[0].status === 'fulfilled') {
            const originalPostData = results[0].value;

            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                let m = {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: originalPostData.destination_text,
                  },
                };
                return m;
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  },
                };
              } else {
                return message;
              }
            });
          }

          if (results[1].status === 'fulfilled') {
            const messageData = results[1].value;
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: messageData.destination_text,
                };
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: t('We were not able to translate this text.'),
                  error: true,
                };
              } else {
                return message;
              }
            });
          }

          setNarrativeMessages({ ...narrativeMessages, objects: newMessagesList });
        });
      } else {
        translateMessage(messageId, text, nativeLanguage)
        .then((data) => {
          newMessagesList = newMessagesList.map((message) => {
            if (message.id === messageId) {
              return {
                ...message,
                isTranslation: true,
                translated: data.destination_text,
              };
            } else {
              return message;
            }
          });

          setNarrativeMessages({ ...narrativeMessages, objects: newMessagesList });
        })
        .catch((e) => {
          newMessagesList = newMessagesList.map((message) => {
            if (message.id === messageId) {
              return {
                ...message,
                isTranslation: true,
                translated: t('We were not able to translate this text.'),
                error: true,
              };
            } else {
              return message;
            }
          });

          setNarrativeMessages({ ...narrativeMessages, objects: newMessagesList });
        });
      }
    }
  }

  // Actor-specific translation handlers
  const toggleActorTranslate = (messageId, isTranslation) => {
    setActorMessages(prev => ({
      ...prev,
      objects: prev.objects.map(message =>
        message.id === messageId ? { ...message, isTranslation } : message
      )
    }));
  };

  const toggleActorAllToTranslation = (messageIds) => {
    setActorMessages(prevMessages => ({
      ...prevMessages,
      objects: prevMessages.objects.map(message => {
        if (messageIds.includes(message.id)) {
          return {
            ...message,
            isTranslation: true,
            ...(message.content_type === 'COMMENT' && message.parent_message && {
              parent_message: {
                ...message.parent_message,
                isTranslation: true,
              }
            })
          };
        }
        return message;
      })
    }));
  };

  const toggleActorAllToOriginal = (messageIds) => {
    setActorMessages(prevMessages => ({
      ...prevMessages,
      objects: prevMessages.objects.map(message => {
        if (messageIds.includes(message.id)) {
          return {
            ...message,
            isTranslation: false,
            ...(message.content_type === 'COMMENT' && message.parent_message && {
              parent_message: {
                ...message.parent_message,
                isTranslation: false,
              }
            })
          };
        }
        return message;
      })
    }));
  };

  const handleActorBulkTranslation = async (messagesToTranslate, abortController) => {
    const BATCH_SIZE = 5;
    const nativeLanguage = currentUser?.translation_language
      ? currentUser?.translation_language
      : window.navigator.language.split('-', 1)[0];

    const batches = [];
    for (let i = 0; i < messagesToTranslate.length; i += BATCH_SIZE) {
      batches.push(messagesToTranslate.slice(i, i + BATCH_SIZE));
    }

    for (const batch of batches) {
      if (abortController?.current?.aborted) break;
      try {
        await processActorBatch(batch, nativeLanguage, abortController);
      } catch (error) {
        if (error.name === 'AbortError') break;
        console.error('Error processing actor batch:', error);
      }
    }
  };

  const processActorBatch = async (batch, nativeLanguage, abortController) => {
    if (!actorMessages?.objects || abortController?.current?.aborted) return;

    const translationPromises = batch.map(message => {
      const originalPostText = message.content_type === 'COMMENT' ? message.parent_message?.highlighted_text || message.parent_message?.text : '';

      if (originalPostText) {
        return Promise.allSettled([
          translateMessage(message.id, originalPostText, nativeLanguage, null, abortController),
          translateMessage(message.id, message.text, nativeLanguage, null, abortController),
        ]).then(results => ({
          messageId: message.id,
          type: 'comment',
          parentResult: results[0],
          messageResult: results[1]
        }));
      } else {
        return translateMessage(message.id, message.text, nativeLanguage, null, abortController)
          .then(data => ({
            messageId: message.id,
            type: 'message',
            messageResult: { status: 'fulfilled', value: data }
          }))
          .catch(error => ({
            messageId: message.id,
            type: 'message',
            messageResult: { status: 'rejected', reason: error }
          }));
      }
    });

    const batchResults = await Promise.allSettled(translationPromises);
    if (abortController?.current?.aborted || !actorMessages?.objects) return;

    setActorMessages(prevMessages => {
      let newMessagesList = Array.isArray(prevMessages?.objects) ? [...prevMessages.objects] : [];

      batchResults.forEach(result => {
        if (result.status === 'fulfilled') {
          const { messageId, type, parentResult, messageResult } = result.value;

          newMessagesList = newMessagesList.map(message => {
            if (message?.id === messageId) {
              let updatedMessage = { ...message, isTranslation: true };

              if (type === 'comment' && parentResult) {
                if (parentResult.status === 'fulfilled') {
                  updatedMessage.parent_message = {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: parentResult.value.destination_text,
                  };
                } else {
                  updatedMessage.parent_message = {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  };
                }
              }

              if (messageResult.status === 'fulfilled') {
                updatedMessage.translated = messageResult.value.destination_text;
              } else {
                updatedMessage.translated = t('We were not able to translate this text.');
                updatedMessage.error = true;
              }

              return updatedMessage;
            }
            return message;
          });
        }
      });

      return { ...prevMessages, objects: newMessagesList };
    });
  };

  const handleActorBulkTranslateClick = async () => {
    setIsActorBulkTranslationLoading(true);

    const messagesToTranslate = [];
    const alreadyTranslated = [];

    if (actorTranslationControllerRef.current) {
      actorTranslationControllerRef.current.abort();
    }
    actorTranslationControllerRef.current = new AbortController();

    actorMessages.objects.forEach(message => {
      if (message.translated) {
        alreadyTranslated.push(message);
      } else {
        messagesToTranslate.push(message);
      }
    });

    if (messagesToTranslate.length === 0) {
      const messageIds = alreadyTranslated.map(msg => msg.id);
      toggleActorAllToTranslation(messageIds);
      setIsActorBulkTranslationLoading(false);
      return;
    }

    if (alreadyTranslated.length > 0) {
      toggleActorAllToTranslation(alreadyTranslated.map(msg => msg.id));
    }

    const messageIds = messagesToTranslate.map(msg => msg.id);
    toggleActorAllToTranslation(messageIds);

    try {
      await handleActorBulkTranslation(messagesToTranslate, actorTranslationControllerRef);
    } catch (error) {
      console.error('Error during actor bulk translation:', error);
    } finally {
      setIsActorBulkTranslationLoading(false);
      actorTranslationControllerRef.current = null;
    }
  };

  const handleActorShowOriginalClick = () => {
    if (actorTranslationControllerRef.current) {
      actorTranslationControllerRef.current.abort();
    }
    const translatedMessages = actorMessages.objects.filter(message => message.translated);
    const messageIds = translatedMessages.map(msg => msg.id);
    toggleActorAllToOriginal(messageIds);
    setIsActorBulkTranslationLoading(false);
  };

  const handleActorTranslation = (messageId, text, isTranslation) => {
    const target = actorMessages.objects.find(message => message.id === messageId);
    const originalPostText = target?.content_type === 'COMMENT' ? target?.parent_message?.highlighted_text || target?.parent_message?.text : '';

    if (target.translated) {
      toggleActorTranslate(messageId, isTranslation);
    } else {
      toggleActorTranslate(messageId, true);

      const nativeLanguage = currentUser?.translation_language
        ? currentUser?.translation_language
        : narrative?.parameters?.translate_keywords_query
        ? narrative?.parameters?.keywords_query_origin_language
        : window.navigator.language.split('-', 1)[0];

      if (originalPostText) {
        Promise.allSettled([
          translateMessage(messageId, originalPostText, nativeLanguage),
          translateMessage(messageId, text, nativeLanguage),
        ]).then((results) => {
          setActorMessages(prev => {
            let newMessagesList = [...prev.objects];

            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                let updated = { ...message, isTranslation: true };

                if (results[0].status === 'fulfilled') {
                  updated.parent_message = {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: results[0].value.destination_text,
                  };
                } else {
                  updated.parent_message = {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  };
                }

                if (results[1].status === 'fulfilled') {
                  updated.translated = results[1].value.destination_text;
                } else {
                  updated.translated = t('We were not able to translate this text.');
                  updated.error = true;
                }

                return updated;
              }
              return message;
            });

            return { ...prev, objects: newMessagesList };
          });
        });
      } else {
        translateMessage(messageId, text, nativeLanguage)
          .then((data) => {
            setActorMessages(prev => ({
              ...prev,
              objects: prev.objects.map((message) =>
                message.id === messageId
                  ? { ...message, isTranslation: true, translated: data.destination_text }
                  : message
              )
            }));
          })
          .catch(() => {
            setActorMessages(prev => ({
              ...prev,
              objects: prev.objects.map((message) =>
                message.id === messageId
                  ? { ...message, isTranslation: true, translated: t('We were not able to translate this text.'), error: true }
                  : message
              )
            }));
          });
      }
    }
  };

  // Handler for actor message deletion - refresh actor messages
  const handleActorMessageDelete = (currentPage) => {
    fetchActorMessages(sourceDetailsId, actorMessagesSorting, currentPage);
    fetchSources(sourcesConfig, excludeFlags, currentPage);
  };

  const [selectMenu, setSelectMenu] = useState(false);
  const [typesSelectMenu, setTypesSelectMenu] = useState(false);

  const select = (
    <Select
      className="chart-select"
      classNamePrefix="chart-select"
      onChange={(e) => {
        setPlatform(e);
        setCountryID(null);
        setSourceSubType(null);
      }}
      value={platform}
      options={platformOptions}
      onMenuOpen={() => setSelectMenu(true)}
      onMenuClose={() => setSelectMenu(false)}
      menuIsOpen={selectMenu}
    />
  );

  const typesSelect = (
    <Select
      className={styles.chartSelect}
      classNamePrefix="chartSelect"
      onChange={(e) => {
        setSourceType(e);
        setCountryID(null);
        setSourceSubType(null);
      }}
      onMenuOpen={() => setTypesSelectMenu(true)}
      onMenuClose={() => setTypesSelectMenu(false)}
      menuIsOpen={typesSelectMenu}
      value={sourceType}
      options={sourcesTypes}
    />
  );

  const sortingMessagesMenu = [
    {
      name: t('Newest'),
      value: '-date_publicated',
    },
    {
      name: t('Oldest'),
      value: 'date_publicated',
    },
    {
      name: t('Most viewed'),
      value: '-impressions',
    },
    {
      name: t('Least viewed'),
      value: 'impressions',
    },
     {
      name: t('Highest engagement'),
      value: '-engagement',
    },
    
  ];

  const sortingSourcesMenu = [
    {
      name: t('Highest audience'),
      value: '-audience',
    },
    {
      name: t('Lowest audience'),
      value: 'audience'
    },
    {
      name: t('Most publications'),
      value: '-messages_count',
    },
    {
      name: t('Least publications'),
      value: 'messages_count',
    },
  ];

  
  // Helper function to refresh both sources and messages after deletion
  const handleMessageDelete = (currentPage) => {
    cancelTranslateAll();
    fetchNarrativeMessages(messagesConfig, excludeFlags, currentPage);
    fetchSources(sourcesConfig, excludeFlags, currentPage);
  };

  const tabslist = [
    {
      title: t('Actors'),
      data: narrativeSources,
      onPageChange: (newPage) => fetchSources(sourcesConfig, excludeFlags, newPage),
      content: (
        <NarrativeSourcesTable
          sources={narrativeSources}
          sorting={sourcesSorting}
          type={
            narrativeWidgetConfigOpinions === 'messages'
              ? 'is_bot'
              : typesSelect.props.value.value
          }
          handleSourceSortingClick={(sorting) => setSourcesSorting(sorting)}
          handleSourceClick={setSourceDetailsId}
          isLoading={isSourcesLoading}
          isShare={isShare}
        />
      ),
    },
    {
      title: t('Publications'),
      data: narrativeMessages,
      onPageChange: (newPage) => {
        setIsBulkTranslationLoading(false);
        setIsBulkTranslation(false);
        fetchNarrativeMessages(messagesConfig, excludeFlags, newPage);
      },
      content: (
        <ModalMessagesTable
          messages={narrativeMessages}
          sorting={messagesSorting}
          showSortingInTable={false}
          handleMessagesSortingClick={(sorting) => setMessagesSorting(sorting)}
          handleTranslation={(messageId, text, isTranslation) =>
            handleTranslation(messageId, text, isTranslation)
          }
          isBulkTranslation={isBulkTranslation}
          setIsBulkTranslation={(bool) => setIsBulkTranslation(bool)}
          isBulkTranslationLoading={isBulkTranslationLoading}
          handleBulkTranslateClick={handleBulkTranslateClick}
          handleShowOriginalClick={handleShowOriginalClick}
          isLoading={isMessagesLoading}
          isShare={isShare}
          narrative={narrative}
          event={event}
          opinion={opinion}
          onMessageDelete={() => handleMessageDelete(tabslist[tablistIndex]?.data?.page)}
          showLoadMore={hasMoreCursorPages(narrativeMessages)}
          isLoadingMore={isLoadingMore}
          onLoadMore={() =>
            fetchNarrativeMessages(messagesConfig, excludeFlags, 1, { cursor: narrativeMessages.cursor })
          }
        />
      ),
    },
  ];

  if(narrative?.id === 58692 && (countryID && countryID.length > 0)) {
    tabslist.pop();
    tabslist.push({
      title: t('Opinions'),
      data: opinions,
      onPageChange: (newPage) => fetchSources(sourcesConfig, excludeFlags, newPage),
      content: (
        <ModalOpinionsTable
          storyId={eventIdsOptions[0]}
          messages={opinions}
          sorting={messagesSorting}
          handleMessagesSortingClick={(sorting) => setMessagesSorting(sorting)}
          handleTranslation={(messageId, text) =>
            handleTranslation(messageId, text)
          }
          isLoading={isOpinionsLoading}
          isShare={isShare}
          narrative={narrative}
        />
      ),
    });
  }

  useEffect(() => {
    const container = document.querySelector('#treechart-modal-controls');
    if(typesSelectMenu === true ) {
      let content = document.querySelector(".chartSelect__menu");
      if(container.offsetHeight < content.offsetHeight) {
        container.style.height=container.offsetHeight + content.offsetHeight + "px";
      }
    } else if (selectMenu === true){
      let content = document.querySelector(".chart-select__menu");
      if(container.offsetHeight < content.offsetHeight) {
        container.style.height=container.offsetHeight + content.offsetHeight + "px";
      }
    } else {
      container.style.height = ' ';
    }

 }, [typesSelectMenu, selectMenu])

  // let showAddToButtons = (audienceRange || type?.value === 'is_verified' || (type?.value === 'is_bot' && subType)) || !showAddToActions ? false : true;

  const activeData = showSourceDetails ? actorMessages : tabslist[tablistIndex]?.data;
  let showPagination = !isCursorPaged(activeData) && activeData?.total > 1;

  return (
    <Modal
      isVisible={infoModal}
      title={title ? title : t('Actors analysis')}
      content={
        <>
          {isChart ? (
            ''
          ) : (
            <div className={styles.modalSelects}>
              {select}
              {typesSelect}
            </div>
          )}
          {!showSourceDetails ? (
            <div className={styles.modalSearchPanel}>
              <DropdownMenu
                isOpen={dropdownMenu}
                onClick={() => setDropdownMenu(!dropdownMenu)}
                menuClassName="modalSorting"
                buttonName={`${t('Sort by')}: ${t(
                  showSourceDetails
                    ? sortingMessagesMenu.find(
                        (item) => item.value === actorMessagesSorting,
                      )?.name
                    : tablistIndex === 0
                    ? sortingSourcesMenu.find(
                        (item) => item.value === sourcesSorting,
                      )?.name
                    : sortingMessagesMenu.find(
                        (item) => item.value === messagesSorting,
                      )?.name,
                )}`}
                icon={<ChevronIcon />}
                frontIcon={<SortIcon />}
              >
                <Radiobutton
                  itemsList={
                    showSourceDetails
                      ? sortingMessagesMenu
                      : tablistIndex === 0
                      ? sortingSourcesMenu
                      : sortingMessagesMenu
                  }
                  current={
                    showSourceDetails
                      ? actorMessagesSorting
                      : tablistIndex === 0
                      ? sourcesSorting
                      : messagesSorting
                  }
                  onChange={(value) => {
                    if (showSourceDetails) {
                      setActorMessagesSorting(value);
                    } else if (tablistIndex === 0) {
                      setSourcesSorting(value);
                    } else {
                      setMessagesSorting(value);
                    }
                  }}
                />
              </DropdownMenu>
            </div>
          ) : (
            ''
          )}
          <div className={styles.modalControls} id="treechart-modal-controls">
            {showSourceDetails ? (
              <ActorDetails
                sourceData={sourceData}
                onReturnClick={() => handleCloseSourceDetails()}
                onAddToCase={() => onAddToCase(true, sourceDetailsId)}
                onAddToGroup={() => onAddToGroup(true, sourceDetailsId)}
                showReturnButton={!source}
                isShare={isShare}
              >
                <ModalMessagesTable
                  messages={actorMessages}
                  sorting={actorMessagesSorting}
                  handleMessagesSortingClick={(sorting) =>
                    setActorMessagesSorting(sorting)
                  }
                  handleTranslation={(messageId, text, isTranslation) =>
                    handleActorTranslation(messageId, text, isTranslation)
                  }
                  isBulkTranslation={isActorBulkTranslation}
                  setIsBulkTranslation={(bool) =>
                    setIsActorBulkTranslation(bool)
                  }
                  isBulkTranslationLoading={isActorBulkTranslationLoading}
                  handleBulkTranslateClick={handleActorBulkTranslateClick}
                  handleShowOriginalClick={handleActorShowOriginalClick}
                  isLoading={isActorMessagesLoading}
                  isShare={isShare}
                  narrative={narrative}
                  event={event}
                  opinion={opinion}
                  onMessageDelete={() =>
                    handleActorMessageDelete(actorMessages?.page)
                  }
                  showLoadMore={hasMoreCursorPages(actorMessages)}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={() =>
                    fetchActorMessages(sourceDetailsId, actorMessagesSorting, 1, actorMessages.cursor)
                  }
                />
              </ActorDetails>
            ) : (
              <Tabs
                tabslist={tabslist}
                heading=""
                defaultIndex={
                  tabIndex ? tabIndex : sourceId || sourceGroupIds ? 1 : 0
                }
                onIndexChange={(index) => {
                  setTablistIndex(index);
                  if (index === 1) {
                    ampli.track({
                      event_type: 'Open content tab in actors popup',
                      event_properties: {
                        user_id: currentUser?.id,
                        workspace_id: currentUser?.workspace_id,
                        narrative_id: narrative?.id,
                        actor_type: type?.value,
                      },
                    });
                  }
                }}
              />
            )}
          </div>
        </>
      }
      footer={
        <div className={styles.modalFooter}>
        {showPagination && <div className={styles.modalControls}>
          <Paginator
            size={10}
            page={
              showSourceDetails
                ? actorMessages?.page
                : tabslist[tablistIndex]?.data?.page
            }
            total={
              showSourceDetails
                ? actorMessages?.total
                : tabslist[tablistIndex]?.data?.total
            }
            onPageChange={(newPage) =>
              showSourceDetails
                ? fetchActorMessages(
                    sourceDetailsId,
                    actorMessagesSorting,
                    newPage,
                  )
                : tabslist[tablistIndex]?.onPageChange(newPage)
            }
            id={`widgetContentModal_${widgetName ? widgetName : ''}`}
          />
        </div>}
        {isShare === true || !showAddToActions || showSourceDetails ? (
            ''
          ) : (
            <div className={styles.modalActions}>
              <button
                className={styles.saveButton}
                onClick={() => {
                  onAddToGroup(true);
                }}
              >
                <SourceGroupIcon />
                {t('Add actors to group')}
              </button>
              <button
                className={styles.saveButton}
                onClick={() => {
                  onAddToCase(true);
                }}
              >
                <AddToIcon />
                {t('Add publications to case')}
              </button>
            </div>
          )}
        </div>
        
      }
      onClose={() => {
        onChange(false);
      }}
      className={styles.chartTreeModal}
    />
  );
};
